import React, { useMemo, useCallback } from 'react';
import { Card, CardContent, Stack, Typography, Box } from '@mui/material';
import { merge } from 'lodash';
import ReactApexChart from 'react-apexcharts';
import { BaseOptionChart } from './charts';
import { getLocalShortHour } from '../utils/datetimeUtils';
import { xAxisFormatter } from '../utils/chartUtils';

const GRID_CONFIG = {
  DAYS_BEFORE: 1,
  DAYS_AFTER: 1,
  AXIS_TICKS_HOURS: 4,
  INTERVAL_MINS: 15
};

const MS_PER_INTERVAL = GRID_CONFIG.INTERVAL_MINS * 60 * 1000;
const COLORS = ['#FF9800', '#4CAF50', '#2196F3', '#E91E63', '#9C27B0', '#00BCD4'];
const UNCONTROLLABLE_COLOR = '#9E9E9E';

export default function DispatchSummary({
  scheduledCharging = [],
  scheduledDischarging = [],
  summerChart = [],
  disChargeMin = 0,
  charges = [],
  discharges = [],
  netLoad = [],
  smartmeterData = [],
  buildingforecast = [],
  targetdemand,
  currentDateTime = new Date(),
  chargerBreakdownSeries = [],
  showAnnotation = true,
  showMissingDataLine = true,
  showBreakdown = true,
}) {

  // ---------------- TIME RANGE ----------------
  const { chartStartTime, chartEndTime, masterGrid } = useMemo(() => {
    const start = new Date(currentDateTime);
    start.setDate(start.getDate() - GRID_CONFIG.DAYS_BEFORE);
    start.setHours(0, 0, 0, 0);

    const end = new Date(currentDateTime);
    end.setDate(end.getDate() + GRID_CONFIG.DAYS_AFTER);
    end.setHours(23, 59, 59, 999);

    const startTs = Math.floor(start.getTime() / MS_PER_INTERVAL) * MS_PER_INTERVAL;
    const endTs = Math.floor(end.getTime() / MS_PER_INTERVAL) * MS_PER_INTERVAL;

    const grid = [];
    for (let ts = startTs; ts <= endTs; ts += MS_PER_INTERVAL) {
      grid.push(ts);
    }

    return { chartStartTime: startTs, chartEndTime: endTs, masterGrid: grid };
  }, [currentDateTime]);

  // ---------------- HIGH-PERFORMANCE DATA PROCESSOR ----------------
  const processed = useMemo(() => {
    // 1. Convert any data source into a quick-access Unix-to-Value map
    const createLookup = (data) => {
      const lookup = {}; // Using plain object for faster key access than Map
      if (!Array.isArray(data)) return lookup;
      
      for (let i = 0; i < data.length; i++) {
        const d = data[i];
        const rawTs = new Date(d?.date).getTime();
        if (isNaN(rawTs)) continue;

        // SNAP: Align raw timestamp to the nearest grid interval
        const bucket = Math.round(rawTs / MS_PER_INTERVAL) * MS_PER_INTERVAL;
        
        if (!lookup[bucket]) {
          lookup[bucket] = { sum: 0, count: 0 };
        }
        lookup[bucket].sum += (d.value || 0);
        lookup[bucket].count++;
      }
      return lookup;
    };

    // 2. Map the master grid to the lookup values
    const formatFromGrid = (lookup) => {
      return masterGrid.map(ts => {
        const entry = lookup[ts];
        // If data exists for this bucket, calculate average, otherwise null
        const yValue = entry ? Math.round((entry.sum / entry.count) * 100) / 100 : null;
        return { x: ts, y: yValue };
      }).filter(item => showMissingDataLine || item.y !== null);
    };

    // 3. Process all series
    const results = {
      cUp: formatFromGrid(createLookup(charges)),
      dUp: formatFromGrid(createLookup(discharges)),
      sdUp: formatFromGrid(createLookup(scheduledDischarging)),
      scUp: formatFromGrid(createLookup(scheduledCharging)),
      smUp: formatFromGrid(createLookup(smartmeterData)),
      bfUp: formatFromGrid(createLookup(buildingforecast)),
      nlUp: formatFromGrid(createLookup(netLoad)),
      bdUp: formatFromGrid(createLookup(smartmeterData)),
      priceUp: formatFromGrid(createLookup(summerChart)),
    };

    // Optimized breakdown processing
    results.breakdown = (chargerBreakdownSeries || []).map((charger, i) => {
      const rawData = charger.charge || charger.data || [];
      const lookup = createLookup(rawData);
      return {
        name: `Sched: ${charger.evse_id || i}`,
        type: 'column',
        data: formatFromGrid(lookup),
        color: charger.controllable !== false ? COLORS[i % 6] : UNCONTROLLABLE_COLOR
      };
    });

    return results;
  }, [
    masterGrid, charges, discharges, scheduledCharging, scheduledDischarging, 
    smartmeterData, buildingforecast, netLoad, summerChart, 
    chargerBreakdownSeries, showMissingDataLine
  ]);

  // ---------------- CHART OPTIONS ----------------
  const baseOptions = useMemo(() => merge(BaseOptionChart(), {
    xaxis: { type: "datetime", labels: { datetimeUTC: false } },
    stroke: { curve: 'straight', width: 1, connectNulls: true },
    tooltip: { shared: true, intersect: false }
  }), []);

  const getBaseOptions = useCallback((yTitle, unit, isDecimal = true) => {
    const annotations = showAnnotation && currentDateTime ? [{
      x: currentDateTime.getTime(),
      strokeDashArray: 5,
      opacity: 0.5,
      label: {
        orientation: 'vertical',
        position: "top",
        text: getLocalShortHour(currentDateTime),
        offsetY: -10,
        style: { color: "#fff", background: "#FF9999" }
      }
    }] : [];

    return merge({}, baseOptions, {
      annotations: { xaxis: annotations },
      xaxis: {
        min: chartStartTime,
        max: chartEndTime,
        tickAmount: Math.floor((chartEndTime - chartStartTime) / (3600000 * GRID_CONFIG.AXIS_TICKS_HOURS)),
        labels: {
          formatter: (val) => {
            if (!val || isNaN(val)) return '';
            if (val >= chartEndTime - MS_PER_INTERVAL) {
              const d = new Date(val);
              return `${d.toLocaleString('en-US', { month: 'short' })} ${String(d.getDate()).padStart(2, '0')} 11:59 PM`;
            }
            return xAxisFormatter?.(val) ?? '';
          }
        }
      },
      yaxis: {
        title: { text: yTitle },
        labels: {
          formatter: (val) => val != null ? `${val.toFixed(isDecimal ? 2 : 0)} ${unit}` : val
        }
      }
    });
  }, [baseOptions, chartStartTime, chartEndTime, currentDateTime, showAnnotation]);

  // ---------------- SERIES LOGIC ----------------
  const isViewingBreakdown = showBreakdown && processed.breakdown.length > 0;

  const dispatchSeries = useMemo(() => {
    if (isViewingBreakdown) {
      return [
        ...processed.breakdown,
        { name: 'Total Charging', data: processed.cUp, color: '#000080', type: 'line' },
        { name: 'Total Discharging', data: processed.dUp, color: 'purple', type: 'line' }
      ];
    }
    return [
      { name: 'Charging', data: processed.cUp, color: '#000080' },
      { name: 'Discharging', data: processed.dUp, color: 'purple' },
      { name: 'Scheduled Discharging', data: processed.sdUp, color: '#FF6666' },
      { name: 'Scheduled Charging', data: processed.scUp, color: '#28a745' }
    ];
  }, [isViewingBreakdown, processed]);

  const targetDemandSeries = useMemo(() => 
    processed.nlUp.map(d => ({ x: d.x, y: targetdemand })), 
  [processed.nlUp, targetdemand]);

  return (
    <Card variant="outlined" sx={{ width: '100%' }}>
      <CardContent>
        <Typography component="h2" variant="subtitle2" gutterBottom>
          Dispatch summary
        </Typography>
        <Stack spacing={4}>
          <Box>
            <ReactApexChart
              type="line"
              height={220}
              series={dispatchSeries}
              options={merge(getBaseOptions('Charging & Discharging (kW)', 'kW'), {
                yaxis: { min: disChargeMin },
                chart: { stacked: isViewingBreakdown, animations: { enabled: false } },
                stroke: {
                  width: isViewingBreakdown ? dispatchSeries.map(s => s.type === 'column' ? 0 : 1) : 1
                },
                plotOptions: { bar: { columnWidth: '80%', borderRadius: 2 } }
              })}
            />
          </Box>
          <Box>
            <ReactApexChart
              type="line"
              height={220}
              series={[
                { name: 'Building demand', data: processed.bdUp, color: '#007bff' },
                { name: 'Meter demand', data: processed.smUp, color: 'purple' },
                { name: 'Building demand forecast', data: processed.bfUp, color: '#28a745' },
                { name: 'Forecasted Net load', data: processed.nlUp, color: '#C89B00' },
                { name: 'Target Demand', data: targetDemandSeries, color: 'gray' }
              ]}
              options={merge(getBaseOptions('Site Electric Load (kW)', 'kW', false), {
                chart: { animations: { enabled: false } },
                stroke: { dashArray: [0, 0, 0, 0, 5] }
              })}
            />
          </Box>
          <Box>
            <ReactApexChart
              type="line"
              height={220}
              series={[{ name: 'Summer Price', data: processed.priceUp, color: '#2600ff' }]}
              options={merge(getBaseOptions('Summer Price (USD)', 'USD'), {
                chart: { animations: { enabled: false } },
                stroke: { curve: 'smooth', width: 1 }
              })}
            />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}